import { computed, ref, watch } from 'vue';

import CaptainAssistantAPI from 'dashboard/api/captain/assistant';

const TREND_SUFFIX = { percent: '%', point: ' pts', absolute: '' };

const resolveTrendGood = (trendValue, direction) => {
  if (direction === 'neutral' || trendValue === 0) return null;
  return direction === 'up' ? trendValue > 0 : trendValue < 0;
};

const formatDuration = hours =>
  hours >= 100 ? `${Math.round(hours / 24)}d` : `${hours}h`;

export const useAgentPerformance = ({ assistantId, t }) => {
  const selectedRange = ref('this_month');
  const stats = ref(null);
  const isLoading = ref(false);
  const error = ref('');
  const selectedMetric = ref({ metric: '', label: '', value: '' });
  const isDrilldownOpen = ref(false);
  let requestId = 0;

  const metricFor = (
    statKey,
    formatValue,
    direction,
    trendKind = 'percent'
  ) => {
    const data = stats.value?.[statKey];
    if (!data) return { value: '—', trend: '', trendGood: null };

    const sign = data.trend > 0 ? '+' : '';
    return {
      value: formatValue(data.current),
      trend: `${sign}${data.trend}${TREND_SUFFIX[trendKind]}`,
      trendGood: resolveTrendGood(data.trend, direction),
    };
  };

  const metrics = computed(() => [
    {
      key: 'handled',
      metric: 'conversations_handled',
      label: t('CAPTAIN.OVERVIEW.METRICS.HANDLED.LABEL'),
      hint: t('CAPTAIN.OVERVIEW.METRICS.HANDLED.HINT'),
      ...metricFor(
        'conversations_handled',
        value => value.toLocaleString(),
        'up'
      ),
    },
    {
      key: 'autoResolution',
      metric: 'auto_resolution_rate',
      label: t('CAPTAIN.OVERVIEW.METRICS.AUTO_RESOLUTION.LABEL'),
      hint: t('CAPTAIN.OVERVIEW.METRICS.AUTO_RESOLUTION.HINT'),
      ...metricFor('auto_resolution_rate', value => `${value}%`, 'up', 'point'),
    },
    {
      key: 'handoff',
      metric: 'handoff_rate',
      label: t('CAPTAIN.OVERVIEW.METRICS.HANDOFF.LABEL'),
      hint: t('CAPTAIN.OVERVIEW.METRICS.HANDOFF.HINT'),
      ...metricFor('handoff_rate', value => `${value}%`, 'down', 'point'),
    },
    {
      key: 'reopen',
      metric: 'reopen_rate',
      label: t('CAPTAIN.OVERVIEW.METRICS.REOPEN.LABEL'),
      hint: t('CAPTAIN.OVERVIEW.METRICS.REOPEN.HINT'),
      ...metricFor('reopen_rate', value => `${value}%`, 'down', 'point'),
    },
    {
      key: 'hoursSaved',
      label: t('CAPTAIN.OVERVIEW.METRICS.HOURS_SAVED.LABEL'),
      hint: t('CAPTAIN.OVERVIEW.METRICS.HOURS_SAVED.HINT'),
      ...metricFor('hours_saved', formatDuration, 'up'),
    },
    {
      key: 'depth',
      label: t('CAPTAIN.OVERVIEW.METRICS.DEPTH.LABEL'),
      hint: t('CAPTAIN.OVERVIEW.METRICS.DEPTH.HINT'),
      ...metricFor(
        'conversation_depth',
        value => value.toFixed(1),
        'neutral',
        'absolute'
      ),
    },
  ]);

  const fetchStats = async () => {
    if (!assistantId.value) return;

    requestId += 1;
    const currentRequestId = requestId;
    isLoading.value = true;
    error.value = '';
    try {
      const { data } = await CaptainAssistantAPI.getStats({
        assistantId: assistantId.value,
        range: selectedRange.value,
      });
      if (currentRequestId === requestId) stats.value = data;
    } catch (fetchError) {
      if (currentRequestId !== requestId) return;
      stats.value = null;
      error.value =
        fetchError?.message ||
        t('CAPTAIN_EXPERIENCE.WORKSPACE.PERFORMANCE_LOAD_ERROR');
    } finally {
      if (currentRequestId === requestId) isLoading.value = false;
    }
  };

  const openMetric = metric => {
    if (!metric.metric) return;
    selectedMetric.value = {
      metric: metric.metric,
      label: metric.label,
      value: metric.value,
    };
    isDrilldownOpen.value = true;
  };

  const closeDrilldown = () => {
    isDrilldownOpen.value = false;
  };

  watch([assistantId, selectedRange], fetchStats, { immediate: true });

  return {
    selectedRange,
    stats,
    isLoading,
    error,
    metrics,
    selectedMetric,
    isDrilldownOpen,
    fetchStats,
    openMetric,
    closeDrilldown,
  };
};
